import { ref, computed, watch, toRaw, type Ref, type ComputedRef } from 'vue';
import type { EditHomologationFormData } from './editHomologation.types';
import { toNumberOrUndefined, fromNumberOrUndefined, deepEqual } from '@/shared/lib';
import { OBSERVATIONS_MAX_LENGTH } from './editHomologation.constants';

const sortHomologationTrainingSelectionIds = (ids: string[]): string[] =>
  [...ids].sort((leftId, rightId) => leftId.localeCompare(rightId, undefined, { numeric: true }));

const homologationTrainingSelectionsEqual = (
  currentSelectionIds: string[],
  baselineSelectionIds: string[]
): boolean => {
  const sortedCurrentSelection = sortHomologationTrainingSelectionIds(currentSelectionIds);
  const sortedBaselineSelection = sortHomologationTrainingSelectionIds(baselineSelectionIds);
  return (
    sortedCurrentSelection.length === sortedBaselineSelection.length &&
    sortedCurrentSelection.every((id, index) => id === sortedBaselineSelection[index])
  );
};

interface FormValidationDeps {
  selectedPositions: Ref<string[]>;
  selectedUsers: Ref<string[]>;
  isLoading: Ref<boolean>;
}

interface HomologationFormState {
  elaborationUserId: ComputedRef<number | string>;
  elaborationPositionId: ComputedRef<number | string>;
  approvalUserId: ComputedRef<number | string>;
  approvalPositionId: ComputedRef<number | string>;
  verificationUserId: ComputedRef<number | string>;
  verificationPositionId: ComputedRef<number | string>;
  hasTrainingString: ComputedRef<string>;
  initialFormData: Ref<EditHomologationFormData | null>;
  hasChanges: ComputedRef<boolean>;
  arePositionsValid: ComputedRef<boolean>;
  effectiveDateError: ComputedRef<string | null>;
  isEffectiveDateValid: ComputedRef<boolean>;
  isObservationsValid: ComputedRef<boolean>;
  isFormValid: ComputedRef<boolean>;
  canSave: ComputedRef<boolean>;
  setElaborationUserId: (value: number | string) => void;
  setElaborationPositionId: (value: number | string) => void;
  setApprovalUserId: (value: number | string) => void;
  setApprovalPositionId: (value: number | string) => void;
  setVerificationUserId: (value: number | string) => void;
  setVerificationPositionId: (value: number | string) => void;
  setHasTraining: (value: string) => void;
  saveInitialState: () => void;
}

export function useHomologationFormState(
  formData: EditHomologationFormData,
  validationDeps: FormValidationDeps
): HomologationFormState {
  const initialFormData = ref<EditHomologationFormData | null>(null);
  const initialTrainingSelectedPositions = ref<string[]>([]);
  const initialTrainingSelectedUsers = ref<string[]>([]);

  const elaborationUserId = computed(() =>
    fromNumberOrUndefined(formData.elaborationEditedByUserId)
  );
  const elaborationPositionId = computed(() =>
    fromNumberOrUndefined(formData.elaborationEditedByPositionId)
  );
  const approvalUserId = computed(() => fromNumberOrUndefined(formData.approvalUserId));
  const approvalPositionId = computed(() => fromNumberOrUndefined(formData.approvalPositionId));
  const verificationUserId = computed(() => fromNumberOrUndefined(formData.verificationUserId));
  const verificationPositionId = computed(() =>
    fromNumberOrUndefined(formData.verificationPositionId)
  );
  const hasTrainingString = computed(() => (formData.hasTraining ? 'true' : 'false'));

  const setElaborationUserId = (value: number | string): void => {
    const newUserId = toNumberOrUndefined(value);
    if (newUserId !== formData.elaborationEditedByUserId) {
      formData.elaborationEditedByPositionId = undefined;
    }
    formData.elaborationEditedByUserId = newUserId;
  };

  const setElaborationPositionId = (value: number | string): void => {
    formData.elaborationEditedByPositionId = toNumberOrUndefined(value);
  };

  const setApprovalUserId = (value: number | string): void => {
    const newUserId = toNumberOrUndefined(value);
    if (newUserId !== formData.approvalUserId) {
      formData.approvalPositionId = undefined;
    }
    formData.approvalUserId = newUserId;
  };

  const setApprovalPositionId = (value: number | string): void => {
    formData.approvalPositionId = toNumberOrUndefined(value);
  };

  const setVerificationUserId = (value: number | string): void => {
    const newUserId = toNumberOrUndefined(value);
    if (newUserId !== formData.verificationUserId) {
      formData.verificationPositionId = undefined;
    }
    formData.verificationUserId = newUserId;
  };

  const setVerificationPositionId = (value: number | string): void => {
    formData.verificationPositionId = toNumberOrUndefined(value);
  };

  const setHasTraining = (value: string): void => {
    formData.hasTraining = value === 'true';
  };

  const hasChanges = computed(() => {
    if (!initialFormData.value) return false;
    const formChanged = !deepEqual(formData, initialFormData.value);
    if (!formData.hasTraining) {
      return formChanged;
    }
    const trainingSelectionsChanged =
      !homologationTrainingSelectionsEqual(
        validationDeps.selectedPositions.value,
        initialTrainingSelectedPositions.value
      ) ||
      !homologationTrainingSelectionsEqual(
        validationDeps.selectedUsers.value,
        initialTrainingSelectedUsers.value
      );
    return formChanged || trainingSelectionsChanged;
  });

  const isValidNumericId = (id: number | undefined | null): boolean =>
    id != null && typeof id === 'number' && !Number.isNaN(id);

  const arePositionsValid = computed(() => {
    const hasElaborationUser = formData.elaborationEditedByUserId != null;
    const hasElaborationPosition = isValidNumericId(formData.elaborationEditedByPositionId);
    if (hasElaborationUser && !hasElaborationPosition) return false;

    const hasApprovalUser = formData.approvalUserId != null;
    const hasApprovalPosition = isValidNumericId(formData.approvalPositionId);
    if (hasApprovalUser && !hasApprovalPosition) return false;

    const hasVerificationUser = formData.verificationUserId != null;
    const hasVerificationPosition = isValidNumericId(formData.verificationPositionId);
    if (hasVerificationUser && !hasVerificationPosition) return false;

    return true;
  });

  const SP_CIVIL_DAY_FORMATTER = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  const toSpCivilDay = (dateStr: string): string | null => {
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
    const parsed = new Date(dateStr);
    if (Number.isNaN(parsed.getTime())) return null;
    return SP_CIVIL_DAY_FORMATTER.format(parsed);
  };

  const effectiveDateError = computed((): string | null => {
    if (!formData.effectiveDate) return null;

    const effectiveDay = toSpCivilDay(formData.effectiveDate);
    if (!effectiveDay) return null;

    if (formData.previousRevisionEffectiveDate) {
      const previousDay = toSpCivilDay(formData.previousRevisionEffectiveDate);
      if (previousDay && effectiveDay <= previousDay) {
        return 'A data de vigência deve ser posterior à vigência da revisão anterior';
      }
    }

    if (formData.currentVerificationDate) {
      const verificationDay = toSpCivilDay(formData.currentVerificationDate);
      if (verificationDay && effectiveDay < verificationDay) {
        return 'A data de vigência não pode ser anterior à data de verificação';
      }
    }

    return null;
  });

  const isEffectiveDateValid = computed(() => effectiveDateError.value === null);

  const isObservationsValid = computed(() => {
    const obs = (formData.observations ?? '').trim();
    if (formData.hasTraining) {
      return obs.length === 0 || (formData.observations?.length ?? 0) <= OBSERVATIONS_MAX_LENGTH;
    }
    return obs.length > 0 && (formData.observations?.length ?? 0) <= OBSERVATIONS_MAX_LENGTH;
  });

  const areTrainingSessionsComplete = computed(() => {
    if (!formData.hasTraining) return true;

    const sessions = formData.trainingSessionUpdates;
    if (!sessions || sessions.length === 0) return false;

    return sessions.every(
      (session) =>
        session.trainingDate?.trim() &&
        isValidNumericId(session.responsibleMemberId) &&
        isValidNumericId(session.responsibleUserPositionId)
    );
  });

  const isFormValid = computed(() => {
    if (!arePositionsValid.value) return false;
    if (!isEffectiveDateValid.value) return false;
    if (!isObservationsValid.value) return false;

    if (formData.hasTraining) {
      const hasSelectedUsers = validationDeps.selectedUsers.value.length > 0;
      if (!hasSelectedUsers) return false;

      if (!areTrainingSessionsComplete.value) return false;
    }

    return true;
  });

  const canSave = computed(() => {
    return hasChanges.value && isFormValid.value && !validationDeps.isLoading.value;
  });

  const saveInitialState = (): void => {
    initialFormData.value = structuredClone(toRaw(formData));
    initialTrainingSelectedPositions.value = sortHomologationTrainingSelectionIds([
      ...validationDeps.selectedPositions.value,
    ]);
    initialTrainingSelectedUsers.value = sortHomologationTrainingSelectionIds([
      ...validationDeps.selectedUsers.value,
    ]);
  };

  watch(
    () => formData.hasTraining,
    (hasTraining) => {
      if (hasTraining) {
        if (!formData.trainingSessionUpdates || formData.trainingSessionUpdates.length === 0) {
          formData.trainingSessionUpdates = [
            {
              trainingId: 0,
              trainingDate: '',
            },
          ];
        }
      } else {
        formData.trainingSessionUpdates = [];
      }
    }
  );

  return {
    elaborationUserId,
    elaborationPositionId,
    approvalUserId,
    approvalPositionId,
    verificationUserId,
    verificationPositionId,
    hasTrainingString,
    initialFormData,
    hasChanges,
    arePositionsValid,
    effectiveDateError,
    isEffectiveDateValid,
    isObservationsValid,
    isFormValid,
    canSave,
    setElaborationUserId,
    setElaborationPositionId,
    setApprovalUserId,
    setApprovalPositionId,
    setVerificationUserId,
    setVerificationPositionId,
    setHasTraining,
    saveInitialState,
  };
}
